import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { authService, permissionService } from '../services/api';

const PermissionContext = createContext(null);

export const PermissionProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [permissions, setPermissions] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Load user and permissions on mount
  const loadUserAndPermissions = useCallback(async () => {
    const token = localStorage.getItem('token');
    if (!token) {
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError(null);

      // Fetch user info and permissions in parallel
      const [userData, permData] = await Promise.all([
        authService.getCurrentUser(),
        permissionService.getMyPermissions()
      ]);

      setUser(userData);
      setPermissions(permData.permissions);
    } catch (err) {
      console.error('Failed to load user/permissions:', err);
      setError(err.message);
      // If unauthorized, clear token
      if (err.response?.status === 401) {
        localStorage.removeItem('token');
        setUser(null);
        setPermissions(null);
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadUserAndPermissions();
  }, [loadUserAndPermissions]);

  // Check if user has a specific permission
  const can = useCallback((resource, action) => {
    // Not loaded yet
    if (!permissions) {
      return false;
    }

    // Admin flag grants everything
    if (user?.is_admin) {
      return true;
    }

    // Check specific permission
    const resourcePerms = permissions[resource];
    if (!resourcePerms) {
      return false;
    }

    return resourcePerms[action] === true;
  }, [permissions, user]);

  // Check if user has any of the specified permissions
  const canAny = useCallback((checks) => {
    return checks.some(([resource, action]) => can(resource, action));
  }, [can]);

  // Check if user has all of the specified permissions
  const canAll = useCallback((checks) => {
    return checks.every(([resource, action]) => can(resource, action));
  }, [can]);

  // Check if user has a specific role
  const hasRole = useCallback((role) => {
    if (!user) return false;
    if (user.is_admin) return true;
    return user.role === role;
  }, [user]);

  // Check if user has any of the specified roles
  const hasAnyRole = useCallback((roles) => {
    if (!user) return false;
    if (user.is_admin) return true;
    return roles.includes(user.role);
  }, [user]);

  // Check if user is admin
  const isAdmin = useCallback(() => {
    return user?.is_admin === true || user?.role === 'admin';
  }, [user]);

  // Refresh permissions (useful after role change)
  const refreshPermissions = useCallback(async () => {
    await loadUserAndPermissions();
  }, [loadUserAndPermissions]);

  // Clear user and permissions (on logout)
  const clearAuth = useCallback(() => {
    setUser(null);
    setPermissions(null);
  }, []);

  const value = {
    user,
    permissions,
    loading,
    error,
    can,
    canAny,
    canAll,
    hasRole,
    hasAnyRole,
    isAdmin,
    refreshPermissions,
    clearAuth,
  };

  return (
    <PermissionContext.Provider value={value}>
      {children}
    </PermissionContext.Provider>
  );
};

export const usePermissions = () => {
  const context = useContext(PermissionContext);
  if (!context) {
    throw new Error('usePermissions must be used within a PermissionProvider');
  }
  return context;
};

export default PermissionContext;
